/* prettier-ignore */

import { Component, computed, EventEmitter, inject, Input, Output, signal, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NewCustomRouteService } from 'src/app/pages/new-custom-route/new-custom-route.service';
import { RegisterStandaloneService } from 'src/app/pages/register-standalone/register-standalone.service';
import { SendUpdateRouteService } from 'src/app/pages/send-update-route/send-update-route.service';
import { AppDataService } from 'src/app/services/appData.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { RestService } from 'src/app/services/rest.service';
import { ToolsService } from 'src/app/services/tools.service';

@Component({
  selector: 'cicerone-registers-after-before',
  templateUrl: './registers-after-before.component.html',
  styleUrls: ['./registers-after-before.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class RegistersAfterBeforeComponent {
    @Output() public registerBeforeEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() public registerAfterEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Input('isAfterStandalone') isAfterStandalone: boolean = false;
    @Input('isAfter') isAfter: boolean = false;
    @Input('isBefore') isBefore: boolean = false;
    @Input('countries') countries: any;
    @Input('provinces') provinces: any;
    @Input() registerData?: any;
    public registerForm?: FormGroup;
    formData: any;
    arrayResult: any = signal([]);
    apiUrlBase: any;
    translate$: any;
    errors = [];

    public headerLangType = computed(() => this.tools.headerLangType());

    private registerStandaloneService = inject(RegisterStandaloneService); 
    public isError = computed(() => {
        const isBeforeError =  
            (this.registerStandaloneService.showRegisterBeforeError() && this.registerStandaloneService.isErrorFromRegisterBefore()) || 
            (this.sendUpdateService.showRegisterBeforeError() && this.sendUpdateService.isErrorFromRegisterBefore());

        const isAfterError = 
            (this.registerStandaloneService.showRegisterAfterError() && this.registerStandaloneService.isErrorFromRegisterAfter()) ||
            (this.sendUpdateService.showRegisterAfterError() && this.sendUpdateService.isErrorFromRegisterAfter());

        if (this.isBefore && isBeforeError && this.arrayResult())
            return true;
        
        if ((this.isAfter || this.isAfterStandalone) && isAfterError && this.arrayResult())
            return true;

        return false;
    })

    defaultRegisterLangsArray: any = ['ES', 'GB', 'DE', 'FR', 'PT', 'IT'];
    defaultRegisterLangs: any = [ 
        { code: 'ES', icon: 'es', name: 'España' }, 
        { code: 'GB', icon: 'en', name: 'Reino Unido' }, 
        { code: 'DE', icon: 'de', name: 'Alemania' },
        { code: 'FR', icon: 'fr', name: 'Francia' }, 
        { code: 'PT', icon: 'pt', name: 'Portugal'}, 
        { code: 'IT', icon: 'it', name: 'Italia' }
    ];

    constructor(
        public translate: TranslateService, 
        private rest: RestService, 
        private fb: FormBuilder,
        public tools: ToolsService,
        public appData: AppDataService,
        private routeService: NewCustomRouteService,
        public sendUpdateService: SendUpdateRouteService,
        private navigation: NavigationService,
    ){ 
        this.apiUrlBase = this.rest.apiUrlBase;
        this.registerForm = this.fb.group({});
        if (!this.countries)
            this.countries = this.routeService.getCountries();

        if (!this.provinces)
            this.provinces = this.routeService.getProvinces();
    }

    ngOnInit(){
        if(this.translate$) this.translate$.unsubscribe()
        this.translate$ = this.translate.onLangChange.subscribe(() => { 
            this.initForm() 
        });
        this.initForm();
    }

    get f() { return this.registerForm.controls; }


    ngOnDestroy(){
        if (this.translate$) 
            this.translate$.unsubscribe()
    }

    initForm(){
        if (this.isBefore)
            this.registerStandaloneService.resetRegisterBefore();

        if (this.isAfter || this.isAfterStandalone)
            this.registerStandaloneService.resetRegisterAfter();

        this.registerForm.reset();
        this.registerForm = this.fb.group({});
        this.formData = this.registerData?.[this.translate.currentLang]?.map((e, index) => {
            let options = [];
            return {
                key: `${index}-${e.type}`,
                type: e.type,
                title: e.name || e.title || e.type,
                options: e.options || options,
                required: e.required === true
            }
        }) || [];
        if (this.formData?.length == 0) return;
        this.formData.forEach(data => { 
            if (data.type != 'selection_multi'){
                if (data.required){
                    this.registerForm.addControl(data.key, new FormControl('', Validators.required)); 
                }else{
                    this.registerForm.addControl(data.key, new FormControl('')); 
                }
            }else{
                const arrayForm = data.required
                    ? this.fb.group({ items: this.fb.array([], this.atLeastOneSelectedValidator) })
                    : this.fb.group({ items: this.fb.array([]) })

                const itemsFormArray = arrayForm.get('items') as FormArray;
                data.options.forEach(option => {    
                    itemsFormArray.push(this.fb.group({
                        value: option,
                        selected: this.fb.control(false)
                      }))
                });
                this.registerForm.addControl(data.key, arrayForm); 
            }
        });
    }

    atLeastOneSelectedValidator(control: AbstractControl) {
        const formArray = control as FormArray;
        const hasSelected = formArray.controls.some(group => group.get('selected')?.value === true);
        return hasSelected ? null : { atLeastOneRequired: true };
    }

    getMultiControls(key){
        const form = this.registerForm.get(key);
        const formArray = form.get('items') as FormArray
        return formArray.controls;
    }

    setSelectedOption(option: any, key: string): void {
        this.registerForm.patchValue({ key, value: option });
        this.getData();
    }

    focusin(evt) {
        evt.stopPropagation();
    }

    goBack(){
        this.navigation.goBack();
        //window.history.back();
    }

    isMultiError(key: string): boolean {
        const control = this.registerForm.get(key) as FormArray;
        return control && control.invalid && control.touched; 
    }

    getData(){
        const formArray = Object.values(this.registerForm.value);
        const array = this.formData?.map((element, index) => {
            return {...element, answer: formArray.length > index ? formArray[index] : ''}
        });
        
        this.arrayResult.update(prev => array.map(e => {
            if (e.type == "selection_multi") e.answer = e.answer?.items?.filter(e => e.selected)?.map(answer => { return answer.value; })
            return { question: e.title || e.name, type: e.type, answer: e.answer, required: e.required };
        }))

        if (this.isAfter || this.isAfterStandalone){
            this.sendUpdateService.registerAfter.update(prev => this.arrayResult())
            this.registerAfterEmitter.emit(this.arrayResult());
        }

        if (this.isBefore){
            this.sendUpdateService.registerBefore.update(prev => this.arrayResult());
            this.registerBeforeEmitter.emit(this.arrayResult());
        }
        
    }

}